import { Grid } from "@material-ui/core";
import { UIRouter } from "@uirouter/core";
import { UIRouterContext } from "@uirouter/react";
import { getDefaultStore } from "jotai";
import { isUndefined, noop } from "lodash";
import { Component, useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import styled from "styled-components";
import { TextField } from "../../../../components/forms/text";
import { Flex } from "../../../../components/layout";
import { Loader } from "../../../../components/loader";
import { Spinner } from "../../../../components/loader/icon";
import { Permission } from "../../../../components/permission";
import { Widget } from "../../../../components/widget";
import { Pageable, Sort } from "../../../../services/pageable";
import { AppStore } from "../../../../store";
import { useFetchable } from "../../../../store/common/fetchable";
import { Fetchable } from "../../../../store/common/fetchable/helpers";
import { StatusEnum } from "../../../../store/users/models/internal";
import { VerificationActions } from "../../../../store/verification/actions";
import { VerificationDTO } from "../../../../store/verification/models/dto/kyc";
import { Verification } from "../../../../store/verification/reducer";
import { selectedKycStatus } from "../../../participations/all/atoms";
import { AnErrorHasOccured } from "../../../participations/all/base";
import { KycStatusSelectorWrapper } from "../../../participations/all/kyc-status-hook";
import { WidgetAdornment } from "./adornment";
import { PresentationalAllKyc } from "./presentational";

// ---- For Regular User ---
export const AllKyc = () => {
  const data = useFetchable(
    Verification.allKyc,
    useMemo<
      Pageable.T<
        | "firstName"
        | "lastName"
        | "country"
        | "emailAddress"
        | "userStatus"
        | "createdDate"
      >
    >(
      () => ({
        pagination: {
          size: 20,
          page: 0,
        },
        sorting: {
          direction: "desc",
          field: "createdDate",
        },
      }),
      []
    )
  );

  return (
    <Loader data={[data]}>
      {([fulfilled]) => (
        <Widget title="All KYCs" adornment={<WidgetAdornment />}>
          <PresentationalAllKyc kycList={fulfilled} />
        </Widget>
      )}
    </Loader>
  );
};

// ---- With Filters for Admin ----
const Wrapper = styled(Flex).attrs({ centered: true })`
  justify-content: center;
  height: 20em;
`;
interface KYCsComponentPropsFromState {
  data: Fetchable<VerificationDTO.Responses.GetAllKyc.T>;
  pagination: Pageable.T<
    | "firstName"
    | "lastName"
    | "country"
    | "emailAddress"
    | "userStatus"
    | "createdDate"
  >;
  query?: string;
  status?: string;
}
interface KYCsComponentPropsFromDispatch {
  setPagination: (
    data: {
      email: string;
      status?: StatusEnum;
    },
    sorting?: Sort<
      | "firstName"
      | "lastName"
      | "country"
      | "emailAddress"
      | "userStatus"
      | "createdDate"
    >
  ) => VerificationActions.Actions;
}

const connection = connect<
  KYCsComponentPropsFromState,
  KYCsComponentPropsFromDispatch,
  {
    query?: string;
    status?: string;
  },
  AppStore
>(
  (store: AppStore, { query, status }) => ({
    ...store.verification.allKyc,
    query,
    status,
  }),
  {
    setPagination: (
      {
        email,
        status,
      }: {
        email: string;
        status?: StatusEnum;
      },
      sorting
    ) =>
      VerificationActions.setAllKycPagination({
        filters: [
          {
            field: "emailAddress",
            value: email,
          },
          {
            field: "userStatus",
            value: status,
          },
        ]
          .filter(({ value }) => value !== "" && !isUndefined(value))
          .map((x) => x as { field: string; value: string }),
        sorting,
      }),
  }
);

export type KYCsComponentProps = ConnectedProps<typeof connection> & {
  router: UIRouter;
};

type KYCsComponentState = {
  loading: boolean;
  email: string;
  debouncedEmail: string;
  selectedKycStatus?: StatusEnum;
};

class AllKycComponentInner extends Component<
  KYCsComponentProps,
  KYCsComponentState
> {
  readonly subscriptions: Subscription[] = [];
  readonly emailSubject;

  constructor(props: KYCsComponentProps) {
    super(props);
    this.emailSubject = new Subject<string>();

    this.subscriptions.push(
      this.emailSubject.pipe(debounceTime(500)).subscribe((email) => {
        this.props.setPagination(
          {
            email,
            status: this.state.selectedKycStatus,
          },
          this.props.pagination.sorting
        );
        this.setState({ debouncedEmail: email });

        // reload URL
        props.router.stateService.go(".", {
          query: email,
          status: this.state.selectedKycStatus,
        });
      })
    );
  }

  componentWillUnmount() {
    this.subscriptions.forEach((x) => x.unsubscribe());
    this.statusChangeUnsub();
  }

  state = {
    loading: false,
    email: this.props.query || "",
    debouncedEmail: this.props.query || "",
    selectedKycStatus: StatusEnum.keys.find((x) => x == this.props.status),
  } as KYCsComponentState;

  // Listener to status atom change
  statusChangeUnsub = noop;

  componentDidMount() {
    this.statusChangeUnsub = getDefaultStore().sub(selectedKycStatus, () =>
      this.setReviewFilter(getDefaultStore().get(selectedKycStatus))
    );

    this.props.setPagination(
      {
        email: this.state.email,
        status: this.state.selectedKycStatus,
      },
      { field: "createdDate", direction: "desc" } // this will be called on compopnentDidMount
    );
  }

  setReviewFilter = (status: StatusEnum | undefined) => {
    this.props.setPagination(
      {
        email: this.state.email,
        status: status,
      },
      this.props.pagination.sorting
    );
    this.setState({ selectedKycStatus: status });

    // reload URL
    this.props.router.stateService.go(".", {
      query: this.state.email,
      status,
    });
  };

  render() {
    const { data } = this.props;

    let componentToRender = null;

    switch (data.status) {
      case "error":
        componentToRender = <AnErrorHasOccured />;
        break;
      case "loading":
      case "empty":
        componentToRender = (
          <Wrapper>
            <Spinner />
          </Wrapper>
        );
        break;
      case "ready":
        componentToRender = <PresentationalAllKyc kycList={data.data} />;
        break;
    }

    if (this.state.debouncedEmail !== this.state.email) {
      componentToRender = (
        <Wrapper>
          <Spinner />
        </Wrapper>
      );
    }

    return (
      <Widget adornment={<WidgetAdornment />}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <TextField
              value={this.state.email}
              onChange={(e) => {
                this.setState({ email: e.target.value });
                this.emailSubject.next(e.target.value);
              }}
              fullWidth
              type="text"
              label="E-mail"
            />
          </Grid>
          <Permission roles={["ROLE_ADMIN", "ROLE_MERCHANT"]}>
            <Grid item xs={12} md={4}>
              <KycStatusSelectorWrapper />
            </Grid>
          </Permission>
        </Grid>
        {componentToRender}
      </Widget>
    );
  }
}

export const AllKYCsComponent = connection(
  (props: KYCsComponentPropsFromState & KYCsComponentPropsFromDispatch) => (
    <UIRouterContext.Consumer>
      {(router) => <AllKycComponentInner {...props} router={router} />}
    </UIRouterContext.Consumer>
  )
);
